import type { Address } from 'viem';

type GuardianContractConfig = {
  safeExecutorModuleAddress: Address;
  organizationFactoryAddress: Address;
  organizationImplementationAddress: Address;
  accountImplementationAddress: Address;
  implementationWhitelistAddress: Address;
  guardianSafeAddress: Address;
};

/**
 * Guardian contract addresses shared across app/packages.
 * Source: contracts/deployment.toml [factory.arachnid].
 */
const GUARDIAN_CONTRACT_CONFIG_BY_ENVIRONMENT = {
  PROD: {
    safeExecutorModuleAddress: '0xDD71d28FC6070a29083C1609CE33b0392eC72a86',
    organizationFactoryAddress: '0xda3557951E2E25671c7CC77AdD9D33ECbD58566d',
    organizationImplementationAddress:
      '0xD28Bf2CC0A9ea58712dD7dE60A0b5Cad756Ea5E8',
    accountImplementationAddress: '0x3FEB2a8c6a260BDE5bF444146b0f89D045423c2b',
    implementationWhitelistAddress:
      '0x92dD2c3F90a9740Daf28c2FfEBcf16a047795556',
    guardianSafeAddress: '0xf6CBe8B1c4e8A8c953b054A01133cFA1eAF46D2C',
  },
  NON_PROD: {
    safeExecutorModuleAddress: '0xb13BE81032D3bF1d0CE424339F982f0474e94e49',
    organizationFactoryAddress: '0xB2F5370A9dB4EC696B1147893bF75679C99Af461',
    organizationImplementationAddress:
      '0xD28Bf2CC0A9ea58712dD7dE60A0b5Cad756Ea5E8',
    accountImplementationAddress: '0x3FEB2a8c6a260BDE5bF444146b0f89D045423c2b',
    implementationWhitelistAddress:
      '0x0f7F6D5494a1ab91b3e50c8657b08019561C2a13',
    guardianSafeAddress: '0x1560df8edEfe1Cc95f3f715eDD3166247d33962f',
  },
} as const satisfies Record<string, GuardianContractConfig>;

function getGuardianContractConfig(
  environment = process.env.ENVIRONMENT
): GuardianContractConfig {
  return environment === 'PROD'
    ? GUARDIAN_CONTRACT_CONFIG_BY_ENVIRONMENT.PROD
    : GUARDIAN_CONTRACT_CONFIG_BY_ENVIRONMENT.NON_PROD;
}

const guardianContractConfig = getGuardianContractConfig();

export const SAFE_EXECUTOR_MODULE_ADDRESS: Address =
  guardianContractConfig.safeExecutorModuleAddress;
export const ORGANIZATION_FACTORY_ADDRESS: Address =
  guardianContractConfig.organizationFactoryAddress;
export const ORGANIZATION_IMPLEMENTATION_ADDRESS: Address =
  guardianContractConfig.organizationImplementationAddress;
export const ACCOUNT_IMPLEMENTATION_ADDRESS: Address =
  guardianContractConfig.accountImplementationAddress;
export const IMPLEMENTATION_WHITELIST_ADDRESS: Address =
  guardianContractConfig.implementationWhitelistAddress;
export const GUARDIAN_SAFE_ADDRESS: Address =
  guardianContractConfig.guardianSafeAddress;

/** Shared deployment used by SafeExecutorModule for atomic, ordered execution. */
export const BATCHED_TRANSACTION_ADDRESS: Address =
  '0x01761d4aE66ee1Fbfa34801E27bD625123b6347f';

/**
 * Canonical EVM zero address.
 */
export const ZERO_ADDRESS: Address =
  '0x0000000000000000000000000000000000000000';
